import { ICON_PATHS, ICON_PATHS_FILLED } from './iconPaths';

/**
 * O ícone do site. Material Symbols (M3), variante outlined.
 *
 * Antes disto o projeto importava de cinco famílias do react-icons — `fa`, `fa6`, `ai`,
 * `md`, `bi` e `gi` — em 52 arquivos. Além dos cinco traços diferentes na mesma tela,
 * havia o mesmo conceito desenhado de formas distintas: `AiOutlineEye` e `FaEye`,
 * `AiOutlineClose` e `FaTimes`, e um `FaCheck` do fa convivendo com o `FaCheck` do fa6,
 * que não é o mesmo desenho. Aqui há um nome por conceito, e um lugar para trocá-lo.
 *
 * O que sobrou de fora, de propósito: os logotipos de marca (WhatsApp, Pix, Asaas), que
 * continuam vindo do react-icons — logo preenchido é reconhecimento, não peso visual, e
 * redesenhá-lo em contorno quebraria as duas coisas.
 *
 * SVG embutido, e não a webfont de ligature do Google. Além de tirar uma requisição do
 * caminho crítico (o mesmo motivo que tirou o @import da fonte do CSS, ver
 * public/index.html), a webfont escreve o nome do glifo no DOM: um `<span>check</span>`
 * entra no nome acessível do botão que o contém, e os testes que consultam por
 * `getByRole('link', { name: /Enviar/ })` passariam a ler "Enviar arrow_forward".
 */

/**
 * @param {string}  name      nome do Material Symbol (ver scripts/gerar-icones.js)
 * @param {boolean} filled    variante preenchida — reservada ao item de navegação ativo
 * @param {string}  label     quando o ícone É a informação; sem ele o ícone é decorativo
 * @param {string}  className tamanho e cor, via Tailwind
 */
export default function Icon({ name, filled = false, label, className = 'h-5 w-5', ...rest }) {
	// `filled` só vale para quem tem a variante gerada; o resto cai no contorno em vez de
	// sumir da tela. Ícone que desaparece em um estado é mais difícil de notar em revisão
	// do que um que deixa de engrossar.
	const d = (filled && ICON_PATHS_FILLED[name]) || ICON_PATHS[name];

	if (!d) {
		if (process.env.NODE_ENV !== 'production') {
			console.warn(`<Icon name="${name}"> não existe. Adicione em scripts/gerar-icones.js e rode \`npm run gerar:icones\`.`);
		}
		return null;
	}

	// Sem `label` o ícone é decorativo: fica fora da árvore de acessibilidade, e quem
	// nomeia o controle é o texto ao lado ou o aria-label do botão que o envolve. Era o
	// padrão da casa, mas aplicado a mão e esquecido em vários pontos (Home, tabelas do
	// Admin, MinhaConta) — agora é o comportamento que vem de graça.
	const a11y = label
		? { role: 'img', 'aria-label': label }
		: { 'aria-hidden': 'true' };

	return (
		<svg
			viewBox="0 -960 960 960"
			className={className}
			fill="currentColor"
			// A cor vem do `text-*` de quem usa, como já vinha com o react-icons.
			focusable="false"
			{...a11y}
			{...rest}
		>
			<path d={d} />
		</svg>
	);
}
